import React, { useCallback } from "react";
//#region types
import {
  EditableTableFieldProps,
  EditableTableTabFieldProps,
  FormFieldDefinitionType,
  FormFieldProps,
} from "models/propTypes/genericFormProps";
import { FormEnums } from "models/enums";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { FieldArray, FieldArrayRenderProps, FormikProps, useFormikContext } from "formik";
import { Grid, Table, TableBody, TableCell, TableContainer, TableRow } from "@mui/material";
import useCheckHasErrors from "../hooks/useCheckHasErrors";
import useCheckVisibility from "../hooks/useCheckVisibility";
import useFocusField from "../hooks/useFocusField";
import EditableTableRowDraggable from "./editableTable/rowDraggable";
import CheckBox from "../baseFields/checkBox";
import EditableTableRow from "./editableTable/row";
import TabTop from "../../tabs/tabTop";
import Icon from "../../icon";
import TableFooter from "../../tables/tableFooter";
import VerticalFormControl from "../verticalFormControl";
import FormFieldWrapper from "../formFieldWrapper";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import clsx from "clsx";

function EditableTableFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, value, disabled, validationSchema, handleOnBlurCustom, handleOnChangeCustom, readonly } = props;

  const [tabValue, setTabValue] = React.useState(0);
  const [displayRows, setDisplayRows] = React.useState<any[]>([]);
  const [masterRowValues, setMasterRowValues] = React.useState<object>({});
  const [initialFocusTabApplied, setInitialFocusTabApplied] = React.useState<boolean>(false);
  const [reloadKey, setReloadKey] = React.useState<string>("");
  const [selectAll, setSelectAll] = React.useState<boolean>(false);

  const focusField = props.focusField?.split(".");

  const { getHasErrors } = useCheckHasErrors<T>(true);
  const { getFieldHidden } = useCheckVisibility();
  const { hasFocusField } = useFocusField<T>(focusField && focusField[1]);

  const extraProps = field.extraProps as EditableTableFieldProps<any>;
  const { selectionFieldName } = extraProps;
  const allowPagination = !extraProps.allowReOrderingOfRows;

  const maxRowsPerPage = 30;
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPage] = React.useState<number>(extraProps?.rowsPerPage ?? maxRowsPerPage);
  const pageStartIndex = page * rowsPerPage;
  const pageEndIndex = pageStartIndex + rowsPerPage;

  const formikProps = useFormikContext<T>();
  const { values } = formikProps;

  const onPageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const changeRowsPerPageHandler = (rowsPerPage: number) => {
    setPage(0);
    setRowsPage(rowsPerPage);
  };

  const toggleSelectAll = () => {
    const allSelected = !selectAll;
    let updatedRows = [...value];

    updatedRows = updatedRows.map((ur, index) =>
      !ur.disabled && selectionFieldName ? { ...ur, [selectionFieldName]: allSelected } : ur
    );

    setSelectAll(!selectAll);
    formikProps.setFieldValue(name, updatedRows);
    //cannot pass formik props because it is cached with tableComponent
    extraProps.onItemSelectionChange && extraProps.onItemSelectionChange(name, updatedRows);
  };

  const updateSelectAll = () => {
    const updatedRows = [...value];
    const allSelected = updatedRows.every((ur) => selectionFieldName && ur[selectionFieldName]);
    setSelectAll(allSelected);
  };

  React.useEffect(() => {
    setReloadKey(
      field.innerFields
        ? Object.entries(field.innerFields)
            .filter(
              ([key, value]) =>
                value?.type === FormEnums.FormFieldType.EDITABLETABLETAB && !getFieldHidden(value.hidden)
            )
            .map(([key, value]) => key)
            .join("")
        : ""
    );
  }, [values]);

  const tabs = React.useMemo(
    () =>
      field.innerFields
        ? Object.entries(field.innerFields)
            .filter(
              ([key, value]) =>
                value?.type === FormEnums.FormFieldType.EDITABLETABLETAB && !getFieldHidden(value.hidden)
            )
            .map(([key, value]) => value)
        : [],
    [field.innerFields, reloadKey]
  );

  const nonTabbedFields = React.useMemo(
    () =>
      field.innerFields
        ? Object.entries(field.innerFields).filter(
            ([key, value]) => value?.type !== FormEnums.FormFieldType.EDITABLETABLETAB
          )
        : [],
    [field.innerFields]
  );

  const currentTab = tabs[tabValue];
  const currentTabExtraProps = currentTab?.extraProps as EditableTableTabFieldProps<any>;

  const tableFooterLeft: FormFieldDefinitionType<any> =
    currentTabExtraProps?.footerLeftFields ?? extraProps.footerLeftFields ?? {};
  const tableFooterRight: FormFieldDefinitionType<any> = extraProps.footerRightFields ?? {};

  const masterRowConfigurationRefFieldName = currentTabExtraProps?.masterRowConfiguration?.referenceFieldName;

  React.useEffect(() => {
    let uniqueIdOfLastRow;
    if (((value as any) ?? []).length + 1 === displayRows.length && displayRows.length > 0) {
      uniqueIdOfLastRow = (displayRows[displayRows.length - 1] as any)[extraProps.idFieldKey];
    }

    if (!disabled && !readonly && extraProps.defaultRow) {
      addDisplayRows(uniqueIdOfLastRow);
    } else {
      setDisplayRows(value ?? []);
    }

    if (!!selectionFieldName) {
      updateSelectAll();
    }
  }, [value, extraProps.defaultRow, masterRowValues]);

  React.useEffect(() => {
    if (!initialFocusTabApplied && focusField && focusField.length > 1) {
      tabs.some((tabField, index) => {
        if (tabField && hasFocusField(tabField)) {
          setTabValue(index);
          setInitialFocusTabApplied(true);
          return true;
        } else return false;
      });
    }
  }, [focusField]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const addDisplayRows = (uniqueId?: number) => {
    const masterRowAdvancedFields = tabs
      .map((tab) => {
        const refField = (tab?.extraProps as EditableTableTabFieldProps<any>)?.masterRowConfiguration
          ?.referenceFieldName;
        if (refField && values[refField]) return Object.entries(tab?.innerFields ?? {})[0][0];
        else return undefined;
      })
      .filter((f) => f !== undefined);

    const masterRowValuesToApply = masterRowAdvancedFields?.length
      ? Object.keys(masterRowValues)
          .filter((key: string) => !masterRowAdvancedFields.includes(key))
          .reduce((obj: any, key: string) => {
            obj[key] = (masterRowValues as any)[key];
            return obj;
          }, {})
      : masterRowValues;

    const newRow = {
      ...extraProps.defaultRow,
      ...masterRowValuesToApply,
      [extraProps.idFieldKey]: uniqueId ?? UtilsHelper.getUniqueId(),
    };
    return setDisplayRows(value ? [...value, newRow] : [newRow]);
  };

  const onAddNewRow = (arrayHelpers: FieldArrayRenderProps) => {
    arrayHelpers.push({ ...displayRows[displayRows.length - 1] });
    handleOnBlurCustom(name);
    extraProps.onAddNewRow && extraProps.onAddNewRow(formikProps);
  };

  const onRemoveRow = (
    arrayHelpers: FieldArrayRenderProps,
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    row: number
  ) => {
    event.stopPropagation();
    arrayHelpers.remove(row);
    handleOnBlurCustom(name);
    extraProps.onItemRemove && extraProps.onItemRemove(displayRows[row], formikProps);
  };

  const onMasterRowChange = (
    fieldOnChangeHandler: (fieldName: string, fieldValue: any, formikProps: FormikProps<any>) => void,
    fieldName: string,
    fieldValue: any,
    formikProps: FormikProps<any>
  ) => {
    fieldOnChangeHandler(fieldName, fieldValue, formikProps);
    if (masterRowConfigurationRefFieldName) {
      const { values, setFieldValue } = formikProps;

      const hasAdvancedRowConfig = values[masterRowConfigurationRefFieldName as string];
      if (!hasAdvancedRowConfig) {
        const updatedTableValue: any[] = value ? [...value] : [];
        const currentFieldName = Object.entries(currentTab?.innerFields ?? {})[0][0];
        if (currentFieldName)
          updatedTableValue.forEach((item: any) => {
            item[currentFieldName] = fieldValue;
          });
        setFieldValue(name, updatedTableValue);
        setMasterRowValues({ ...masterRowValues, [currentFieldName]: fieldValue });
      }
    }
  };

  const onMasterRowConfigurationChange = (fieldName: string, fieldValue: any, formikProps: FormikProps<any>) => {
    const { setFieldValue } = formikProps;
    const updatedTableValue = value ? [...value] : [];
    const currentFieldName = Object.entries(currentTab?.innerFields ?? {})[0][0];
    let newValue: any;

    if (updatedTableValue.length > 0 && currentFieldName) {
      if (fieldValue) {
        newValue = extraProps.defaultRow[currentFieldName];
        updatedTableValue.forEach((item: any, index: number) => {
          if (index > 0) item[currentFieldName] = newValue;
        });
      } else {
        newValue = updatedTableValue[0][currentFieldName];
        updatedTableValue.forEach((item: any) => {
          item[currentFieldName] = newValue;
        });
      }

      setFieldValue(name, updatedTableValue);
      setMasterRowValues({ ...masterRowValues, [currentFieldName]: newValue });
    }
  };

  const [hoverState, setHoverState] = React.useState<{ index: number; dragAbove: boolean }>();

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setHoverState(undefined);
      const currentValue: any[] = [...value];
      const [removed] = currentValue.splice(dragIndex, 1);
      currentValue.splice(hoverIndex, 0, removed);
      handleOnChangeCustom(name, currentValue);
    },
    [value]
  );

  const hoverRow = useCallback((dragIndex: any, hoverIndex: number) => {
    setHoverState(dragIndex !== hoverIndex ? { index: hoverIndex, dragAbove: dragIndex > hoverIndex } : undefined);
  }, []);

  const getHasMasterRowEnabled = (tabIndex: number) => {
    const tabExtraProps =
      tabIndex && tabs[tabIndex]?.extraProps
        ? (tabs[tabIndex]?.extraProps as EditableTableTabFieldProps<any>)
        : undefined;
    const hasMasterRowEnabled =
      tabExtraProps?.masterRowConfiguration &&
      values[tabExtraProps.masterRowConfiguration.referenceFieldName] ===
        tabExtraProps.masterRowConfiguration.enableWhenEqualTo;

    return hasMasterRowEnabled ?? false;
  };

  const getFieldName = (rowIndex: number, currentFieldName: string, withRowIndex?: boolean) => {
    return (value !== undefined && rowIndex < value.length) || withRowIndex
      ? `${name}[${rowIndex}].${currentFieldName}`
      : `${name}.${currentFieldName}`;
  };

  const RowElement = extraProps.allowReOrderingOfRows ? EditableTableRowDraggable : EditableTableRow;

  const getColumnHeaders = () => {
    const columnCount = nonTabbedFields.length;
    let columnIterator = 0;
    return (
      <TableRow className={cssExports.columnHeaderRow}>
        {nonTabbedFields.map(([key, field], index) => {
          if (columnIterator >= columnCount) {
            return <></>;
          }
          const header = extraProps.headers?.find((h) => h.fieldName === key);
          if (header) {
            if (field?.type === FormEnums.FormFieldType.CHECKBOX) {
              columnIterator++;
              return (
                <TableCell
                  className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.select, header.className)}
                >
                  <CheckBox
                    checked={selectAll}
                    onChange={toggleSelectAll}
                    disabled={extraProps.disableSelection}
                    className={clsx(extraProps.disableSelection ? cssExports.disable : "")}
                  />
                </TableCell>
              );
            }
            columnIterator = columnIterator + (header?.columnSpan ?? 1);
            return (
              <TableCell
                key={`tab-header-${index}`}
                className={clsx(cssExports.tableCell, header.className)}
                colSpan={header.columnSpan}
              >
                {header.label}
              </TableCell>
            );
          }
          return (
            <TableCell
              key={index}
              className={clsx(
                cssExports.blackHighlightText,
                extraProps?.headersNoWrap ? cssExports.whiteSpaceNoWrap : ""
              )}
            >
              {field
                ? field.controlLabel === undefined || typeof field.controlLabel === "string"
                  ? LocalizationHelperInstance.getFormattedMessage(field.controlLabel ?? field.label, field.label)
                  : field.controlLabel
                : ""}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  return field.innerFields ? (
    <Grid
      container
      className={clsx(
        cssExports.tableListContainer,
        cssExports.editableTableContainer,
        extraProps?.isCompactTable ? cssExports.compactTable : "",
        props.field.className
      )}
      direction="column"
    >
      <TableContainer className={cssExports.tableContainerOuter}>
        <Table aria-label="simple table">
          <TableBody className={cssExports.tableBody}>
            {tabs.length > 0 && (
              <TableRow className={cssExports.tabRow}>
                <TableCell
                  key={`tab-header-fixed-start`}
                  colSpan={(extraProps.allowReOrderingOfRows ? 1 : 0) + nonTabbedFields.length}
                ></TableCell>
                <TableCell key={`tab-header-fixed-middle`} colSpan={tabs.length + 1}>
                  <TabTop
                    tabKey={name}
                    value={tabValue ?? 0}
                    onChange={handleTabChange}
                    tabs={tabs.map((item, index) =>
                      item
                        ? {
                            text: (
                              <Grid
                                container
                                direction="row"
                                className={
                                  index !== tabValue && getHasErrors(item, undefined, name)
                                    ? cssExports.tabErrorIndicator
                                    : ""
                                }
                                alignItems="center"
                              >
                                <span>{`${
                                  item.label
                                    ? LocalizationHelperInstance.getFormattedMessage(item.label, item.label)
                                    : ""
                                }`}</span>
                                {index !== tabValue && getHasErrors(item, undefined, name) && (
                                  <Icon icon={IconDefinitionConstants.WExclamation} />
                                )}
                              </Grid>
                            ),
                          }
                        : { text: "" }
                    )}
                  />
                </TableCell>
              </TableRow>
            )}
            {extraProps.showColumnHeaders && getColumnHeaders()}

            <FieldArray
              name={name}
              render={(arrayHelpers) => {
                return (allowPagination ? displayRows.slice(pageStartIndex, pageEndIndex) : displayRows).map(
                  (row: any, rowIndexInner: number) => {
                    const rowIndex = pageStartIndex + rowIndexInner;
                    return (
                      <RowElement
                        key={`tab-row-${row[extraProps.idFieldKey] ?? rowIndex}`}
                        row={row}
                        rowIndex={rowIndex}
                        idFieldKey={extraProps.idFieldKey}
                        disabled={disabled}
                        onClick={
                          value === undefined || rowIndex === value.length ? () => onAddNewRow(arrayHelpers) : undefined
                        }
                        isAddNewRow={value === undefined || rowIndex === value.length}
                        allowReOrderingOfRows={extraProps.allowReOrderingOfRows}
                        nonTabbedFields={nonTabbedFields}
                        tabs={tabs}
                        tabValue={tabValue}
                        errors={props.errors && props.errors[rowIndex]}
                        touched={props.touched && props.touched[rowIndex]}
                        isCompactTable={extraProps?.isCompactTable}
                        validationSchema={validationSchema}
                        rowValues={displayRows[rowIndex]}
                        tableReadonly={readonly}
                        onMasterRowChange={onMasterRowChange}
                        getFieldName={getFieldName}
                        getHasMasterRowEnabled={getHasMasterRowEnabled}
                        moveRow={moveRow}
                        hoverRow={hoverRow}
                        dragHoverState={hoverState}
                        moreActions={extraProps?.moreActions}
                        hideActions={extraProps?.hideActions}
                        removeRowClickHandler={onRemoveRow.bind(null, arrayHelpers)}
                      />
                    );
                  }
                );
              }}
            />
          </TableBody>
        </Table>
        {allowPagination ? (
          <Grid container className={cssExports.tablePaginationContainer}>
            <TableFooter
              total={displayRows.length}
              rowsPerPage={rowsPerPage}
              maxRowsPerPage={maxRowsPerPage}
              currentPage={page}
              advancedPagination
              hideIfSinglePage
              changePageHandler={onPageChange}
              changeRowsPerPageHandler={changeRowsPerPageHandler}
            />
          </Grid>
        ) : (
          <></>
        )}
      </TableContainer>
      {currentTabExtraProps?.masterRowConfiguration && !currentTabExtraProps.masterRowConfiguration.hidden && (
        <Grid item className={cssExports.tableMasterRowController}>
          <VerticalFormControl<T>
            name={currentTabExtraProps.masterRowConfiguration.referenceFieldName}
            field={{
              label: TranslationKeys.Common.EditInRRows,
              type: FormEnums.FormFieldType.TOGGLE,
              onChangeHandler: onMasterRowConfigurationChange,
              disabled: currentTabExtraProps.masterRowConfiguration.disabled,
            }}
          />
        </Grid>
      )}
      <Grid container justifyContent="space-between" alignItems="flex-start">
        <Grid xs item container className={cssExports.tableFooter} spacing={2}>
          {Object.entries(tableFooterLeft).map(([fieldName, field], index) =>
            field && !getFieldHidden(field.hidden) ? (
              <Grid item container key={`footerFieldLeft-${index}`} spacing={2}>
                <FormFieldWrapper
                  name={fieldName}
                  field={field}
                  hideSuccess={props.hideSuccess}
                  hideErrorBubble
                  validationSchema={validationSchema}
                ></FormFieldWrapper>
              </Grid>
            ) : (
              <></>
            )
          )}
        </Grid>
        <Grid xs item container justifyContent={"flex-end"} spacing={2} className={cssExports.tableFooter}>
          {Object.entries(tableFooterRight).map(([fieldName, field], index) =>
            field && !getFieldHidden(field.hidden) ? (
              <Grid item key={`footerFieldLeft-${index}`}>
                <FormFieldWrapper
                  name={fieldName}
                  field={field}
                  hideSuccess={props.hideSuccess}
                  hideErrorBubble
                  validationSchema={validationSchema}
                ></FormFieldWrapper>
              </Grid>
            ) : (
              <></>
            )
          )}
        </Grid>
      </Grid>
    </Grid>
  ) : (
    <></>
  );
}

export default EditableTableFormField;
